import { format } from "date-fns";
import { DayPickerSingleProps } from "react-day-picker";
import { Dispatch, SetStateAction, useState } from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";
import * as SelectPrimitive from "@radix-ui/react-select";
import i18n from "i18next";
import { useTranslation } from "react-i18next";

import { Select, SelectContent, SelectGroup, SelectItem } from "./ui/select";

import { Button } from "@/components/ui/button";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { cn } from "@/lib/utils";
import { getDateFnsLocale } from "@/i18n/getDateFnsLocale.ts";

const getMonthLabel = (monthIndex: number) =>
  format(new Date(0, monthIndex), "LLLL", { locale: getDateFnsLocale(i18n.language) });

interface DatePickerProps {
  selected: DayPickerSingleProps["selected"];
  onSelect: DayPickerSingleProps["onSelect"];
  minDate?: Date;
  maxDate?: Date;
}

const DatePicker = ({
  onSelect,
  selected,
  minDate = new Date(1920, 0),
  maxDate = new Date(),
}: DatePickerProps) => {
  const maxYear = maxDate.getFullYear();
  const minYear = minDate.getFullYear();
  const { t } = useTranslation("common");

  const [calendarMonth, setCalendarMonth] = useState(maxDate.getMonth());
  const [calendarYear, setCalendarYear] = useState(maxYear);

  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button
          className={cn(
            "bg-side-accent pl-3 text-left font-normal",
            !selected && "text-muted-foreground",
          )}
          variant="outline"
        >
          {selected ? format(selected, "dd / MM / yyyy") : <span>{t("pickDate")}</span>}
          <span className="iconify tabler--calendar ml-auto h-4 w-4 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent align="start" className="w-auto p-0">
        <Calendar
          initialFocus
          components={{
            CaptionLabel: () => (
              <CustomCaption
                calendarMonth={calendarMonth}
                calendarYear={calendarYear}
                maxDate={maxDate}
                maxYear={maxYear}
                minYear={minYear}
                setCalendarMonth={setCalendarMonth}
                setCalendarYear={setCalendarYear}
              />
            ),
            IconLeft: () => <ChevronLeft className="h-4 w-4" />,
            IconRight: () => <ChevronRight className="h-4 w-4" />,
          }}
          disabled={(date) => date > maxDate || date < minDate}
          fromDate={minDate}
          locale={getDateFnsLocale(i18n.language)}
          mode="single"
          month={new Date(calendarYear, calendarMonth)}
          selected={selected}
          toDate={maxDate}
          weekStartsOn={1}
          onMonthChange={(value) => {
            setCalendarMonth(value.getMonth());
            setCalendarYear(value.getFullYear());
          }}
          onSelect={onSelect}
        />
      </PopoverContent>
    </Popover>
  );
};

interface CustomCaptionProps {
  calendarMonth: number;
  setCalendarMonth: Dispatch<SetStateAction<number>>;
  calendarYear: number;
  setCalendarYear: Dispatch<SetStateAction<number>>;
  maxDate: Date;
  maxYear: number;
  minYear: number;
}

const CustomCaption = ({
  calendarMonth,
  setCalendarMonth,
  calendarYear,
  setCalendarYear,
  maxDate,
  maxYear,
  minYear,
}: CustomCaptionProps) => {
  const [isMonthOpen, setIsMonthOpen] = useState(false);
  const [isYearOpen, setIsYearOpen] = useState(false);

  return (
    <div className="flex grow items-center justify-center gap-2">
      <Select
        open={isMonthOpen}
        value={calendarMonth.toString()}
        onOpenChange={setIsMonthOpen}
        onValueChange={(value) => {
          setCalendarMonth(Number(value));
        }}
      >
        <SelectPrimitive.Trigger asChild>
          <Button
            className="border-accent border capitalize"
            size="sm"
            variant="ghost"
            onClick={() => {
              setIsMonthOpen(true);
            }}
          >
            {getMonthLabel(calendarMonth)}
          </Button>
        </SelectPrimitive.Trigger>
        <SelectContent>
          <SelectGroup>
            {Array.from({ length: 12 }, (_, monthIndex) => {
              return (
                <SelectItem
                  key={monthIndex}
                  className={"capitalize"}
                  disabled={maxYear === calendarYear ? maxDate.getMonth() < monthIndex : false}
                  value={monthIndex.toString()}
                >
                  {getMonthLabel(monthIndex)}
                </SelectItem>
              );
            })}
          </SelectGroup>
        </SelectContent>
      </Select>
      <Select
        open={isYearOpen}
        value={calendarYear.toString()}
        onOpenChange={setIsYearOpen}
        onValueChange={(value) => {
          setCalendarYear(Number(value));
        }}
      >
        <SelectPrimitive.Trigger asChild>
          <Button
            className="border-accent border"
            size="sm"
            variant="ghost"
            onClick={() => {
              setIsYearOpen(true);
            }}
          >
            {calendarYear}
          </Button>
        </SelectPrimitive.Trigger>
        <SelectContent>
          <SelectGroup>
            {Array.from({ length: maxYear - minYear + 1 }, (_, i) => {
              const year = maxYear - i;

              return (
                <SelectItem key={year} value={year.toString()}>
                  {year}
                </SelectItem>
              );
            })}
          </SelectGroup>
        </SelectContent>
      </Select>
    </div>
  );
};

export default DatePicker;
